// assets/src/jsx/components/Home/ImportDatabaseButton.jsx
import React from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import IntlMessages from 'appUtil/IntlMessages';
import Notification from 'appUtil/Notification';
import {NotificationManager} from 'react-notifications';
import Helpers from '../../../ts/utils/Helpers';
import {decodeImportFile, runDatabaseImport, setPendingImportSchema} from 'appUtil/databaseImport';

// "Import JSON" call-to-action for the Home hero. Picks a .json database export,
// runs the shared import pipeline, then emits `changed` so the Home recent list
// (and the rest of the app) refreshes, and opens the imported database.
class ImportDatabaseButton extends React.Component {

    constructor(props) {
        super(props);
        this.importing = false;
        this.fileInputRef = React.createRef();
        this.openPicker = this.openPicker.bind(this);
        this.onFileSelected = this.onFileSelected.bind(this);
        this.importFromDataUrl = this.importFromDataUrl.bind(this);
    }

    openPicker() {
        if (!this.importing && this.fileInputRef.current) {
            this.fileInputRef.current.click();
        }
    }

    onFileSelected(event) {
        const file = event.target.files[0];
        event.target.value = '';
        if (!file || this.importing) {
            return;
        }
        if (!Helpers.checkUploadFileSizeLimit(file)) {
            Notification.uploadFileLimitError(file.size);
            return;
        }
        const reader = new FileReader();
        reader.onerror = () => {
            Notification.error(<IntlMessages id="database.import.error"/>, <IntlMessages id="database.import.file.invalid"/>);
        };
        reader.onload = (e) => this.importFromDataUrl(e.target.result);
        reader.readAsDataURL(file);
    }

    async importFromDataUrl(dataUrl) {
        const data = decodeImportFile(dataUrl);
        if (!data) {
            // decodeImportFile already logged the decode/JSON reason.
            Notification.error(<IntlMessages id="database.import.error"/>, <IntlMessages id="database.import.file.invalid"/>);
            return;
        }
        // A bare schema (not a full database/group export) → open the create page
        // with that schema pre-loaded, rather than reporting "no databases".
        if (!data.databases && !data.groups && Helpers.validateSchema(data)) {
            setPendingImportSchema(data);
            this.props.history.push('/app/database/create');
            return;
        }
        if (!Helpers.validateImportDatabase(data)) {
            Notification.error(<IntlMessages id="database.import.error"/>, <IntlMessages id="database.import.file.invalid"/>);
            return;
        }
        this.importing = true;
        try {
            const created = await runDatabaseImport(data, {
                registryService: this.props.registryService,
                databaseList: this.props.databaseList,
                databaseGroups: this.props.databaseGroups,
                authUser: this.props.authUser,
                onGroupUpdated: () => this.props.eventBus.emit('database-group-updated'),
            });
            if (!created || created.length === 0) {
                // The file parsed and validated, but had no databases to create
                // (e.g. an export of grouped databases produces `{"databases": []}`).
                // eslint-disable-next-line no-console
                console.warn('[import] no databases were created from the file', data);
                Notification.error(<IntlMessages id="database.import.error"/>, <IntlMessages id="home.import.empty"/>);
                return;
            }
            this.props.eventBus.emit('changed');
            NotificationManager.success(<IntlMessages id="database.imported"/>, <IntlMessages id="home.cta.import"/>, 3000);
            // Open the imported database so the user lands in it (drafts have no
            // dashboard, so go to the editor); otherwise the dashboard.
            const first = created[0];
            this.props.history.push(first.isDraft
                ? `/app/database/${first.id}/modify`
                : `/app/database/${first.id}/dashboard`);
        } catch (err) {
            // A create may have partially succeeded — refresh so any created DBs show.
            this.props.eventBus.emit('changed');
            NotificationManager.error(Helpers.getMessageException(err), <IntlMessages id="database.import.error"/>);
        } finally {
            this.importing = false;
        }
    }

    render() {
        return (
            <button type="button" className="system-button size-m outline" onClick={this.openPicker}>
                <i className="far fa-file-import" aria-hidden="true"/><IntlMessages id="home.cta.import"/>
                <input ref={this.fileInputRef} type="file" accept=".json"
                    style={{display: 'none'}} onChange={this.onFileSelected}/>
            </button>
        );
    }
}

const mapStateToProps = ({database, services, auth}) => ({
    databaseList: database.list,
    databaseGroups: database.groups,
    eventBus: database.eventBus,
    registryService: services.registry,
    authUser: auth.authUser,
});

export default withRouter(connect(mapStateToProps)(ImportDatabaseButton));
